import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  SyntheticEvent,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import clsx from 'clsx';
import { startOfYear } from 'date-fns';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  commaFormat,
  encodeSearchWord,
  getGender,
  timestamp2Localestring,
  TOOLTIP_FONT_SIZE,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_CLASS_USER_STAT_LIST,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import {
  deleteMemberSchoolClass,
  getClassUserStatList,
} from '../../common/api/ApiClassStat';
import { SwConfirmDialog } from '../commonComponent/views/SwConfirmDialog';
import { selectSchoolInfoState } from '../../common/atom';
import { SwDateRangePicker } from '../commonComponent/dateSetting/SwDateRangePicker';

const columns = [
  '닉네임',
  '출석번호',
  '성별',
  '완독 권수',
  '누적 걸음수',
  '평균 걸음수',
  '작성 글',
  '상세 보기',
];

interface Props {}

export const UserStat: React.FC<Props> = () => {
  const [schoolClassInfo, setSchoolClassInfo] = useRecoilState(
    selectSchoolInfoState
  );
  const { loginVal } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formInputRef = useRef<any>();
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestList, setRequestList] = useState<any>();
  const [bookGroupList, setBookGroupList] = useState<any>([]);
  const [bookInfoList, setBookInfoList] = useState<any>([]);
  const [isAdd, setIsAdd] = useState<boolean>(true);
  const [selectedSeq, setSelectedSeq] = useState<number>(-1);
  const [checkedMembers, setCheckedMembers] = useState<string[]>([]);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [rangeDate, setRangeDate] = useState([
    startOfYear(new Date()), // 올해 1월 1일
    new Date(), // 오늘
  ]);

  const [arrowNumber, setArrowNumber] = useState<number>(0);
  const [arrowFinish, setArrowFinish] = useState<number>(-1);
  const [arrowAvg, setArrowAvg] = useState<number>(-1);
  const [arrowTotal, setArrowTotal] = useState<number>(-1);
  const [arrowWrite, setArrowWrite] = useState<number>(-1);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState<string>('nickname');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [isToday, setIsToday] = useState<any>(false);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);

  const {
    data,
    isLoading,
    refetch: refetchClassStatUsers,
  } = useQuery(
    KEY_CLASS_USER_STAT_LIST,
    () => {
      if (schoolClassInfo !== undefined && schoolClassInfo !== null) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
          endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
          page: page,
          rowsPerPage: rowsPerPage,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          sortByNumber: arrowNumber,
          sortByFinish: arrowFinish,
          sortByAvg: arrowAvg,
          sortByTotal: arrowTotal,
          sortByWrite: arrowWrite,
        };
        return getClassUserStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestList(res.statList);
          setTotalRecords(res.totalRecordCount);
        } else {
          requestList([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const prefetchClassStatUsers = () => {
    queryClient.prefetchQuery(KEY_CLASS_USER_STAT_LIST, () => {
      if (schoolClassInfo !== null) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
          endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
          page: page,
          rowsPerPage: rowsPerPage,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          sortByNumber: arrowNumber,
          sortByFinish: arrowFinish,
          sortByAvg: arrowAvg,
          sortByTotal: arrowTotal,
          sortByWrite: arrowWrite,
        };
        return getClassUserStatList(newData);
      }
      return null;
    });
  };

  const downloadClassStatUsers = (param: any) =>
    xapi
      .get(
        `/admin/v1/apis/class-stat/download/users?schoolSeq=${param.schoolSeq}&schoolGrade=${param.schoolGrade}&schoolClass=${param.schoolClass}&startDate=${param.startDate}&endDate=${param.endDate}&page=${param.page}&perPage=${param.rowsPerPage}&searchKey=${param.searchKey}&searchWord=${param.searchWord}`,
        { responseType: 'blob' }
      )
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `class-stat-users.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout((_: any) => {
          window.URL.revokeObjectURL(url);
        }, 600000);
        a.remove();
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('err: ', err);
      });

  const downloadClassStatUserList = () => {
    const newData = {
      schoolSeq: schoolClassInfo.schoolSeq,
      schoolGrade: schoolClassInfo.schoolGrade,
      schoolClass: schoolClassInfo.schoolClass,
      startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
      endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
      page: 0,
      rowsPerPage: 0,
      searchKey: searchKey,
      searchWord: encodeSearchWord(searchKey, searchWord),
    };
    setLoading(true);
    downloadClassStatUsers(newData);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const searchClassStatUsers = () => {
    if (page === 1) {
      setLoading(true);
      refetchClassStatUsers();
    } else {
      setPage(1);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchClassStatUsers();
    }
  };

  const detailUserStat = (idx: number) => {
    const newSeq = requestList[idx].seq;
    setSelectedSeq(newSeq);
    setOpenUserStat(true);
  };

  const { mutate: deleteMemberSchoolClassMutate, isError: isMutateError } =
    useMutation(deleteMemberSchoolClass, {
      onSuccess: () => {
        setOpenSnackbar(true);
        setDialogMessage('탈퇴 처리되었습니다.');
        refetchClassStatUsers();
      },
      onError: error => {
        setOpenSnackbar(true);
        setDialogMessage(
          '탈퇴 처리가 실패했습니다. 다시 시도해주시기 바랍니다.'
        );
      },
    });

  const withdrawMember = () => {
    const newData: any = {
      memberCodes: [...checkedMembers],
    };
    setCheckedAll(false);
    setCheckedMembers([]);
    deleteMemberSchoolClassMutate(newData);
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    setCheckedMembers([]);
    if (event.target.checked) {
      setCheckedAll(true);
      requestList?.map((row: any, idx: number) => {
        setCheckedMembers(current => [...current, row.memberCode]);
      });
    } else {
      setCheckedAll(false);
    }
  };

  const changeHandler = (checked: boolean, id: string) => {
    if (checked) {
      const newData = [...checkedMembers, id];
      setCheckedMembers([...checkedMembers, id]);
      setCheckedAll(newData.length === requestList.length);
    } else {
      setCheckedMembers(checkedMembers.filter(button => button !== id));
      setCheckedAll(false);
    }
  };

  const onClickArrowButton = (idx: number) => {
    setArrowNumber(
      idx === 1 && arrowNumber !== 1
        ? 1
        : idx === 1 && arrowNumber === 1
        ? 0
        : -1
    );
    setArrowFinish(
      idx === 3 && arrowFinish !== 1
        ? 1
        : idx === 3 && arrowFinish === 1
        ? 0
        : -1
    );
    setArrowTotal(
      idx === 4 && arrowTotal !== 1 ? 1 : idx === 4 && arrowTotal === 1 ? 0 : -1
    );
    setArrowAvg(
      idx === 5 && arrowAvg !== 1 ? 1 : idx === 5 && arrowAvg === 1 ? 0 : -1
    );
    setArrowWrite(
      idx === 6 && arrowWrite !== 1 ? 1 : idx === 6 && arrowWrite === 1 ? 0 : -1
    );
  };

  useEffect(() => {
    setLoading(true);
    refetchClassStatUsers();
  }, [arrowNumber, arrowFinish, arrowAvg, arrowTotal, arrowWrite]);

  useEffect(() => {
    setLoading(true);
    refetchClassStatUsers();
  }, [rangeDate]);

  useEffect(() => {
    setLoading(true);
    refetchClassStatUsers();
  }, [page]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      refetchClassStatUsers();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    setSchoolClassInfo(prev => ({
      ...prev,
      educationOfficeSeq: loginVal.value.user.educationOfficeSeq,
      schoolGroupSeq: loginVal.value.user.schoolGroupSeq,
      schoolSeq: loginVal.value.user.schoolSeq,
      schoolGrade: loginVal.value.user.schoolGrade,
      schoolClass: loginVal.value.user.schoolClass,
    }));
  }, [loginVal]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchClassStatUsers();
    }
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <SwClassStatTab
          tabValue={0}
          schoolClassInfo={schoolClassInfo}
          setSchoolClassInfo={setSchoolClassInfo}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            ml: '.5rem',
            gap: 2,
          }}
        >
          <TextField
            sx={{ width: '24rem', marginBottom: '0' }}
            placeholder='닉네임으로 검색'
            value={searchWord}
            onChange={evt => setSearchWord(evt.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton sx={{ pr: 0 }} onClick={searchClassStatUsers}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <SwDateRangePicker
              rangeDate={rangeDate}
              setRangeDate={setRangeDate}
              minStartDate={startOfYear(new Date())}
              maxEndDate={new Date()}
            />
          </Box>
        </Box>
        <Box className='flex_between'>
          <Box>
            <Typography className='title'>
              사용자 수 <span style={{ color: '#2581FF' }}>{totalRecords}</span>
              명
            </Typography>
          </Box>
          <Box>
            <Button
              sx={{ mr: '1rem' }}
              color='warning'
              variant='outlined'
              onClick={() => {
                setOpenConfirm(true);
              }}
            >
              탈퇴
            </Button>
            <Button
              sx={{ mr: '1rem' }}
              color='info'
              variant='outlined'
              onClick={() => {
                downloadClassStatUserList();
              }}
            >
              다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer sx={{ height: '68vh', overflowY: 'scroll' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      onChange={e => {
                        handleSelectAllClick(e);
                      }}
                      checked={checkedAll}
                    />
                  </TableCell>
                  {columns.map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                      <Button
                        sx={{ padding: '0 .1rem', minWidth: '0' }}
                        onClick={() => onClickArrowButton(idx)}
                      >
                        {' '}
                        {(idx === 1 && arrowNumber !== 1) ||
                        (idx === 3 && arrowFinish !== 1) ||
                        (idx === 4 && arrowTotal !== 1) ||
                        (idx === 5 && arrowAvg !== 1) ||
                        (idx === 6 && arrowWrite !== 1) ? (
                          <ArrowDropDownIcon sx={{ fontSize: '2rem' }} />
                        ) : (idx === 1 && arrowNumber === 1) ||
                          (idx === 3 && arrowFinish === 1) ||
                          (idx === 4 && arrowTotal === 1) ||
                          (idx === 5 && arrowAvg === 1) ||
                          (idx === 6 && arrowWrite === 1) ? (
                          <ArrowDropUpIcon sx={{ fontSize: '2rem' }} />
                        ) : (
                          ''
                        )}
                      </Button>
                      {idx === 5 && (
                        <Tooltip
                          title={
                            <Typography fontSize={TOOLTIP_FONT_SIZE}>
                              걸음수가 기록되지 않은 날은 평균 계산에서
                              제외됩니다.
                            </Typography>
                          }
                          followCursor
                        >
                          <HelpOutlineIcon
                            sx={{ fontSize: '1rem', color: '#999999' }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requestList &&
                  requestList.length > 0 &&
                  requestList
                    .map((row: any) => [
                      row.nickname,
                      row.memberNumber,
                      getGender(row.gender),
                      row.finishCount !== null ? row.finishCount : '-',
                      row.totalStep !== null
                        ? commaFormat(`${row.totalStep}`)
                        : '-',
                      row.avgStep !== null
                        ? commaFormat(`${row.avgStep}`)
                        : '-',
                      row.writeCount !== null ? row.writeCount : '-',
                      '',
                    ])
                    .map((row: any, idx: number) => (
                      <TableRow key={idx.toString()}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            id={row.id}
                            onChange={e => {
                              changeHandler(
                                e.currentTarget.checked,
                                requestList[idx].memberCode
                              );
                            }}
                            checked={checkedMembers.includes(
                              requestList[idx].memberCode
                            )}
                          />
                        </TableCell>
                        {columns.map((column: any, i: number) =>
                          i === columns.length - 1 ? (
                            <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                              <Button
                                variant='outlined'
                                color='info'
                                onClick={() => detailUserStat(idx)}
                              >
                                보기
                              </Button>
                            </TableCell>
                          ) : (
                            <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                              {row[i]}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={Math.ceil(totalRecords / rowsPerPage)}
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwConfirmDialog
        contents='선택한 사용자를 탈퇴 처리 하시겠습니까?'
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={() => {
          withdrawMember();
          setOpenConfirm(false);
        }}
        confirm={1}
      />
      <SwUserStatDetail
        memberSeq={selectedSeq}
        open={openUserStat}
        onClose={() => setOpenUserStat(false)}
      />
      {loading && <SwLoading />}
    </>
  );
};
